/**
 * Lightweight haptic feedback helper for app-like touch responses.
 *
 * Uses the Web Vibration API. Silently no-ops when:
 *   - the API isn't available (iOS Safari, desktop)
 *   - the user prefers reduced motion
 *   - the page isn't user-activated yet (browser will simply ignore)
 *
 * Keep patterns very short — long vibrations feel cheap, not premium.
 */

type HapticIntensity = 'tap' | 'soft' | 'success' | 'warn';

const PATTERNS: Record<HapticIntensity, number | number[]> = {
  tap: 8,        // light single tick — buttons, toggles
  soft: 4,       // barely-there — option pills, scroll snaps
  success: [10, 40, 18], // add-to-cart / checkout open
  warn: [14, 30, 14],    // remove / error
};

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function haptic(intensity: HapticIntensity = 'tap'): void {
  if (typeof navigator === 'undefined') return;
  if (typeof navigator.vibrate !== 'function') return;
  if (prefersReducedMotion()) return;
  try {
    navigator.vibrate(PATTERNS[intensity]);
  } catch {
    /* ignore — some browsers throw outside user gesture */
  }
}

export function useHaptics() {
  return haptic;
}
